import { createActionCreators } from 'immer-reducer';

import { BirthdayWish, ProfileReducer } from '@/store/reducers/profile';
import { AsyncAction } from '@/store/actions/common';
import moment from 'moment';
import { combineSickLeaves, sickLeavesActions } from '@/store/actions/sick-leaves';
import { combineCalendarVacations, combineVacations, vacationsActions } from '@/store/actions/vacations';
import { revalidate } from '@/store/responses';

export const profileActions = createActionCreators(ProfileReducer);

export type ProfileActions =
  | ReturnType<typeof profileActions.setProjects>
  | ReturnType<typeof profileActions.setVacations>
  | ReturnType<typeof profileActions.setInventory>
  | ReturnType<typeof profileActions.setSickLeaves>
  | ReturnType<typeof profileActions.setVacationsCalendar>
  | ReturnType<typeof profileActions.setBirthdayWish>
  | ReturnType<typeof profileActions.cleanBirthdayWish>
  | ReturnType<typeof profileActions.clearCalendarVacations>;

// An edit form, so always fresh: filled from the stored copy, a changed response landing
// afterwards would refill the text area over what the person is typing.
export const getBirthdayWish = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/birthday-wish',
      () => mainProtectedApi.getBirthdayWish(),
      (wish) => dispatch(profileActions.setBirthdayWish(wish)),
      { slot: 'profileReducer.birthdayWish', skipStored: true },
    );
  } catch (e) {
    console.log(e);
  }
};

/** Resolves to whether the save went through; the reason, if not, reaches the global toast. */
export const saveBirthdayWish = (wish: string): AsyncAction<Promise<boolean>> => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const saved = await mainProtectedApi.saveBirthdayWish(wish) as unknown as BirthdayWish;
    dispatch(profileActions.setBirthdayWish(saved));
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getMyProject = (): AsyncAction => async (
  dispatch,
  getState,
  { mainProtectedApi },
) => {
  try {
    const { _id: userId } = getState().userReducer.user!;

    await revalidate(
      `/projects/user/${userId}`,
      () => mainProtectedApi.getUserProjects(userId),
      (projects) => dispatch(profileActions.setProjects(projects)),
      { slot: 'profileReducer.projects' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getVacations = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const currentVacations = await mainProtectedApi.getCurrentVacations();
    const futureVacations = await mainProtectedApi.getFutureVacations();
    const recentVacations = await mainProtectedApi.getRecentVacations();

    const vacations = recentVacations.concat(currentVacations).concat(futureVacations);
    vacations.sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());

    const combinedVacations = combineVacations(vacations);
    dispatch(profileActions.setVacations(vacations, combinedVacations));
  } catch (e) {
    console.log(e);
  }
};

export const getVacationsCalendar = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/vacations/calendar',
      () => mainProtectedApi.getVacationsCalendar(),
      ({ vacations, holidays, sickLeaves }) => {
        vacations.sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());
        const combinedVacations = combineCalendarVacations(vacations);
        const combinedSickLeave = combineSickLeaves(sickLeaves);
        dispatch(vacationsActions.setHolidays(holidays));
        dispatch(profileActions.setVacationsCalendar(vacations, combinedVacations));
        dispatch(sickLeavesActions.setSickLeaves(sickLeaves, combinedSickLeave));
      },
      // holidays is also written by getHolidays, sickLeaves by getUserSickLeaves.
      { slot: ['vacationsReducer.holidays', 'profileReducer.vacationsCalendar', 'sickLeaveReducer.sickLeaves'] },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getAllVacations = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const vacations = await mainProtectedApi.getAllVacations();
    vacations.sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());
    const combinedVacations = combineVacations(vacations);
    dispatch(profileActions.setVacations(vacations, combinedVacations));
  } catch (e) {
    console.log(e);
  }
};

export const getSickLeaves = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const sickLeaves = await mainProtectedApi.getAllSickLeaves();

    const combinedSickLeave = combineSickLeaves(sickLeaves);

    dispatch(profileActions.setSickLeaves(sickLeaves, combinedSickLeave));
  } catch (e) {
    console.log(e);
  }
};

export const getMyInventory = (): AsyncAction => async (
  dispatch,
  getState,
  { mainProtectedApi },
) => {
  try {
    const { _id: userId } = getState().userReducer.user!;

    await revalidate(
      `/inventory/user/${userId}`,
      () => mainProtectedApi.getUsersInventory(userId),
      (inventory) => dispatch(profileActions.setInventory(inventory)),
      { slot: 'profileReducer.inventory' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const clearCalendarVacations = (): AsyncAction => async (
  dispatch,
) => {
  try {
    dispatch(profileActions.clearCalendarVacations());
  } catch (e) {
    console.log(e);
  }
};
